import { addMonths, format, subMonths } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import MultiDatePicker from 'react-multi-date-picker';
import { FormHelperText, InputLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import { includes, isEmpty, isEqual } from 'lodash';

const StyledDatePicker = styled(MultiDatePicker)(({ theme }) => ({
  '& .rmdp-week-day': {
    color: theme.palette.primary.main,
    cursor: 'default',
    fontSize: '12px',
    fontWeight: 500,
  },
  '& .rmdp-input:focus': {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '& .rmdp-range': {
    backgroundColor: theme.palette.primary.main,
    boxShadow: '0 0 3px #fff',
    color: '#fff',
  },
  '& .rmdp-range-hover': {
    backgroundColor: theme.palette.primary.light,
  },
  '& .rmdp-day.rmdp-today span': {
    backgroundColor: theme.palette.primary.light,
    color: '#fff',
    borderColor: '#fff',
  },
  '& .rmdp-day:hover': {
    backgroundColor: theme.palette.primary.light,
  },
  '& .rmdp-day:not(.rmdp-disabled,.rmdp-day-hidden) span:hover': {
    backgroundColor: theme.palette.primary.light,
    color: '#fff',
  },
  '& .rmdp-arrow-container:hover': {
    backgroundColor: theme.palette.primary.light,
    boxShadow: '0 0 3px #8798ad',
  },
  '& .rmdp-arrow': {
    border: `solid ${theme.palette.primary.light}`,
    borderWidth: '0 2px 2px 0',
    display: 'inline-block',
    marginTop: '5px',
    padding: '3px',
    width: '3px',
  },
}));

const DateRangePickerFieldWithLimit = ({
  label,
  initialValue,
  onDateChange,
  maxMonths = 3,
  maxDate,
}) => {
  const [selectedDate, setSelectedDate] = useState([]);
  const [isValidDate, setIsValidDate] = useState(true);

  const displayedHelperText = !isValidDate ? 'Please select a valid date' : '';

  const [prevDateRange, setPrevDateRange] = useState(null);

  useEffect(() => {
    if (!initialValue) {
      setSelectedDate([]);
    } else {
      setSelectedDate(
        initialValue.map((d) => {
          if (typeof d === 'string' && d.includes('T')) {
            return new Date(d);
          }
          const [year, month, day] = d.split('-').map(Number);
          return new Date(year, month - 1, day);
        })
      );
    }
  }, [initialValue]);

  const shouldDisableDate = (date) => {
    if (!maxMonths || !selectedDate || selectedDate?.length === 0) return false;

    if (selectedDate.length === 1) {
      const selectedDateObj =
        typeof selectedDate[0].toDate === 'function' ? selectedDate[0].toDate() : selectedDate[0];
      const currentDate = date.toDate();

      const minAllowedDate = subMonths(selectedDateObj, maxMonths);
      const maxAllowedDate = addMonths(selectedDateObj, maxMonths);

      return currentDate < minAllowedDate || currentDate > maxAllowedDate;
    }

    return false;
  };

  const onHandleDateChange = (data) => {
    setSelectedDate(data);
    const dates = data?.map((sDate) => sDate.toDate?.().toString());

    if (!includes(dates, 'Invalid Date')) {
      setIsValidDate(true);

      const dateRange = dates?.map((date) => format(new Date(date), 'yyyy-MM-dd'));
      setPrevDateRange(dateRange);

      const startDate = !isEmpty(dateRange) && dateRange[0];
      const endDate = !isEmpty(dateRange) && dateRange[1];

      if (!isEqual(dateRange, prevDateRange) && !isEqual(startDate, endDate)) {
        onDateChange(dateRange);
      }
    } else {
      setIsValidDate(false);
    }
  };

  return (
    <>
      <InputLabel shrink id='' sx={{ fontSize: '1.2em', mb: 0.5 }}>
        {label}
      </InputLabel>
      <StyledDatePicker
        name='dateRange'
        placeholder='MM-DD-YYYY ~ MM-DD-YYYY'
        format='MM-DD-YYYY'
        style={{
          width: '100%',
          minWidth: 210,
          height: '35px',
          padding: 10,
          margin: 0,
          marginBottom: 0,
          marginTop: 0,
          borderColor: !isValidDate && 'red',
        }}
        calendarPosition='bottom-center'
        range
        rangeHover
        maxDate={maxDate}
        value={selectedDate}
        onChange={onHandleDateChange}
        mapDays={({ date }) => ({
          disabled: shouldDisableDate(date),
        })}
      />
      <FormHelperText sx={{ color: 'red', pl: 1, mb: 0, mt: 0.5 }}>
        {displayedHelperText}
      </FormHelperText>
    </>
  );
};

export default DateRangePickerFieldWithLimit;

DateRangePickerFieldWithLimit.propTypes = {
  initialValue: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.string.isRequired,
  maxDate: PropTypes.instanceOf(Date),
  onDateChange: PropTypes.func.isRequired,
  maxMonths: PropTypes.number,
};

DateRangePickerFieldWithLimit.defaultProps = {
  initialValue: undefined,
  maxDate: undefined,
  maxMonths: undefined,
};
